/*
  It's So Easy 相談室
  - <consult-compose>：相談ページの書く欄。2段階の表示、入口で見ていたものを添える、携帯会社のアドレスへの案内
  - <consult-product>：商品ページのブロック。サイズや色を選び直したら、在庫切れの表示を切り替える
  - <consult-thanks>：送ったあと。見ていたページへ戻るボタンと、Klaviyo・GA4 への記録
  - GA4：入口のブロック、相談ページ、送信完了の利用をイベントで送る（下の「GA4」を参照）
  形を変えるのは JS が動いたときだけにし、動かないときもふつうのフォームとして送れるようにしている。
*/

const STORAGE_KEY = 'consult:last';
const FROM_LABELS = {
  pdp: '商品ページ',
  diagnosis: '診断結果',
  gift: 'ギフトのページ',
  mail: 'メルマガ',
  followup: '購入後のメール',
  letter: 'サンクスレター',
  ig: 'Instagram',
  page: 'ページ',
};
const BACK_LABELS = {
  diagnosis: '診断結果に戻る',
  gift: 'ギフトのページに戻る',
};
const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const CARRIER_DOMAINS =
  /@(docomo\.ne\.jp|ezweb\.ne\.jp|au\.com|softbank\.ne\.jp|i\.softbank\.jp|ymobile\.ne\.jp|uqmobile\.jp|rakumail\.jp)$/i;

/* ---- 保存 ---- */

function session() {
  try {
    return window.sessionStorage;
  } catch (error) {
    return null;
  }
}

function readSaved() {
  try {
    return JSON.parse(session()?.getItem(STORAGE_KEY) || 'null');
  } catch (error) {
    return null;
  }
}

function save(value) {
  try {
    session()?.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch (error) {
    // 保存できないときは、戻るボタンと送信の記録だけを省く
  }
}

/* ---- Klaviyo ---- */

function identify(email) {
  try {
    if (typeof window.klaviyo?.identify === 'function') {
      window.klaviyo.identify({ email });
    } else if (Array.isArray(window._learnq)) {
      window._learnq.push(['identify', { $email: email }]);
    }
  } catch (error) {
    // Klaviyo が無いときは何もしない
  }
}

function track(name, properties) {
  try {
    if (typeof window.klaviyo?.track === 'function') {
      window.klaviyo.track(name, properties);
    } else if (Array.isArray(window._learnq)) {
      window._learnq.push(['track', name, properties]);
    }
  } catch (error) {
    // Klaviyo が無いときは何もしない
  }
}

/* ---- GA4 ----
  「Google & YouTube」アプリがページに読み込む gtag を使い、送り先を GA4（テーマ設定「相談室」の測定 ID）
  だけに絞って送る（Google 広告のタグには送らない）。相談の中身・お名前・メールアドレスは送らない。
  gtag の準備ができる前のイベントはためておき、準備ができたら送る。
*/

const pendingEvents = [];
let waitTimer = 0;

function ga4Id() {
  return document.querySelector('[data-consult-ga4]')?.getAttribute('data-consult-ga4')?.trim() || '';
}

function gtagReady(id) {
  if (typeof window.gtag !== 'function' || !Array.isArray(window.dataLayer)) return false;
  return window.dataLayer.some((entry) => entry && entry[0] === 'config' && entry[1] === id);
}

function flushEvents() {
  const id = ga4Id();
  if (!id) {
    pendingEvents.length = 0;
    return;
  }
  if (gtagReady(id)) {
    while (pendingEvents.length) {
      const [name, params] = pendingEvents.shift();
      try {
        window.gtag('event', name, { ...params, send_to: id });
      } catch (error) {
        // 計測できなくても画面の動きは止めない
      }
    }
    return;
  }
  if (waitTimer) return;
  let tries = 0;
  waitTimer = window.setInterval(() => {
    tries += 1;
    if (!gtagReady(id) && tries < 40) return;
    window.clearInterval(waitTimer);
    waitTimer = 0;
    if (gtagReady(id)) flushEvents();
    else pendingEvents.length = 0;
  }, 500);
}

function sendEvent(name, params = {}) {
  const clean = {};
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    clean[key] = typeof value === 'string' ? value.slice(0, 100) : value;
  });
  pendingEvents.push([name, clean]);
  flushEvents();
}

function attachedOf(context) {
  if (context.product) return 'product';
  if (context.result) return 'result';
  return 'none';
}

/** 入口のブロック（商品ページ・診断結果・ギフト）の情報 */
function blockParams(element) {
  const block = element.closest('[data-consult-track="block"]');
  if (!(block instanceof HTMLElement)) return {};
  return {
    consult_entry: block.dataset.consultEntry,
    consult_mode: block.dataset.consultMode,
    consult_topic: block.dataset.consultTopic,
    consult_product: block.dataset.consultProduct,
    consult_result: block.dataset.consultResult,
  };
}

/** consult_block_view：ブロックの半分以上（または画面の半分以上）が見えたら、1ページ1回 */
function observeBlocks() {
  const blocks = document.querySelectorAll('[data-consult-track="block"]');
  if (!blocks.length || !('IntersectionObserver' in window)) return;
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const enough =
          entry.intersectionRatio >= 0.5 || entry.intersectionRect.height >= window.innerHeight * 0.5;
        if (!enough) return;
        observer.unobserve(entry.target);
        sendEvent('consult_block_view', blockParams(entry.target));
      });
    },
    { threshold: [0, 0.25, 0.5, 0.75, 1] }
  );
  blocks.forEach((block) => observer.observe(block));
}

/** ボタン・事例の商品・Q&A と「続きのやりとり」を開いたとき */
function onDocumentClick(event) {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const cta = target.closest('[data-consult-cta]');
  if (cta) {
    sendEvent('consult_cta_click', blockParams(cta));
    return;
  }

  const productLink = target.closest('[data-consult-product-link]');
  if (productLink instanceof HTMLElement) {
    sendEvent('consult_case_product_click', {
      consult_case: productLink.closest('[data-consult-case]')?.getAttribute('data-consult-case'),
      consult_product: productLink.dataset.consultProductLink,
      consult_entry: productLink.closest('[data-consult-entry]')?.getAttribute('data-consult-entry'),
    });
    return;
  }

  const summary = target.closest('summary');
  const details = summary?.parentElement;
  // 押した時点で閉じているもの＝これから開くものだけを数える
  if (!(details instanceof HTMLDetailsElement) || details.open) return;
  if (details.matches('.consult-qa__item')) {
    sendEvent('consult_qa_open', {
      ...blockParams(details),
      consult_case: details.dataset.consultCase,
      consult_position: Number(details.dataset.consultPosition) || undefined,
    });
  } else if (details.matches('.consult-case__more')) {
    sendEvent('consult_case_more', {
      consult_case: details.closest('[data-consult-case]')?.getAttribute('data-consult-case'),
      consult_entry: details.closest('[data-consult-entry]')?.getAttribute('data-consult-entry'),
    });
  }
}

/* ---- 相談ページの書く欄 ---- */

function withWidth(src, width) {
  if (!src) return '';
  const url = src.startsWith('//') ? `https:${src}` : src;
  return `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
}

class ConsultCompose extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = 'true';

    this.topics = this.readTopics();
    this.context = this.readContext();
    this.applyPlaceholder();
    this.fillFields();
    this.showAttached();
    this.setupSteps();

    // テーマのフォームで送ったあと（お礼を同じページに出したとき）は、相談ページの表示として数えない
    if (!this.querySelector('consult-thanks')) {
      sendEvent('consult_room_view', { ...this.eventParams(), consult_attached: attachedOf(this.context) });
    }

    this.querySelectorAll('[data-consult-dm]').forEach((link) => {
      link.addEventListener('click', () => sendEvent('consult_dm_click', this.eventParams()));
    });
  }

  eventParams() {
    return { consult_from: this.context.from || 'none', consult_topic: this.context.topic };
  }

  readTopics() {
    const script = this.querySelector('script[data-consult-topics]');
    if (!script) return {};
    try {
      return JSON.parse(script.textContent || '{}') || {};
    } catch (error) {
      return {};
    }
  }

  readContext() {
    const params = new URLSearchParams(window.location.search);
    const get = (key) => (params.get(key) || '').trim();
    const topic = get('topic');

    let entryUrl = '';
    try {
      const referrer = new URL(document.referrer);
      if (referrer.origin === window.location.origin && referrer.pathname !== window.location.pathname) {
        entryUrl = referrer.pathname + referrer.search;
      }
    } catch (error) {
      // 外から来たときは戻り先を持たない
    }

    return {
      topic: this.topics[topic] ? topic : '',
      topicTitle: this.topics[topic]?.title || '',
      from: get('from'),
      product: get('product'),
      variant: get('variant'),
      result: get('result'),
      productTitle: '',
      productUrl: '',
      entryUrl,
    };
  }

  /** soldOutLabel は在庫切れのものから来たときだけ渡す（「サイズ M」、サイズや色が無い商品は「この商品」） */
  applyPlaceholder(soldOutLabel = '') {
    const body = this.querySelector('[data-consult-body]');
    if (!(body instanceof HTMLTextAreaElement)) return;
    const placeholder = this.topics[this.context.topic]?.placeholder;
    if (soldOutLabel) {
      // 在庫切れから来たときは、相談の軸の記入例より、入荷と近いものを聞く例を出す
      body.placeholder = `例）${soldOutLabel}がほしかったのですが、入荷はいつごろになりそうですか？ほかに合いそうなものがあれば知りたいです。`;
    } else if (placeholder) {
      body.placeholder = placeholder;
    } else if (this.context.product) {
      // 相談の軸が分からない商品から来たときは、見ていた商品に合わない記入例を出さない
      body.placeholder = '';
    }
  }

  setField(name, value) {
    this.querySelectorAll(`[data-consult-field="${name}"]`).forEach((input) => {
      if (input instanceof HTMLInputElement) input.value = value;
    });
  }

  fillFields() {
    const { from, topicTitle, result, product } = this.context;
    const fromLabel = FROM_LABELS[from];
    this.setField('from', from ? (fromLabel ? `${from}（${fromLabel}）` : from) : '相談ページ');
    this.setField('topic', topicTitle);
    this.setField('result', result);
    this.setField('product', product);
  }

  async showAttached() {
    const card = this.querySelector('[data-consult-attached]');
    if (!(card instanceof HTMLElement)) return;
    const label = card.querySelector('[data-consult-attached-label]');
    const title = card.querySelector('[data-consult-attached-title]');
    const image = card.querySelector('[data-consult-attached-image]');
    if (!label || !title) return;

    if (this.context.product) {
      const product = await this.fetchProduct(this.context.product);
      if (!product) return;
      const variant = (product.variants || []).find((item) => String(item.id) === this.context.variant);
      const variantLabel = variant ? this.variantLabel(product, variant) : '';

      this.context.productTitle = product.title;
      this.context.productUrl = variant ? `${product.url}?variant=${variant.id}` : product.url;
      this.setField(
        'product',
        [product.title, variantLabel, `${window.location.origin}${this.context.productUrl}`].filter(Boolean).join(' ')
      );

      const src = variant?.featured_image?.src || product.featured_image;
      if (image instanceof HTMLImageElement && src) {
        image.src = withWidth(src, 120);
        image.hidden = false;
      }
      if (variant && variant.available === false) this.applyPlaceholder(variantLabel || 'この商品');
      label.textContent = variantLabel ? `見ていた商品・${variantLabel}` : '見ていた商品';
      title.textContent = product.title;
      card.hidden = false;
    } else if (this.context.result) {
      label.textContent = '診断結果';
      title.textContent = this.context.result;
      card.hidden = false;
    }
  }

  /** 「サイズ S」のように、選んでいたサイズや色を名前つきで返す */
  variantLabel(product, variant) {
    if (!variant || variant.title === 'Default Title') return '';
    const names = (product.options || []).map((option) => (typeof option === 'string' ? option : option?.name));
    const values = Array.isArray(variant.options) ? variant.options : [];
    if (!names.length || names.length !== values.length) return variant.title;
    return values
      .map((value, index) => (String(value).includes(names[index]) ? value : `${names[index]} ${value}`))
      .join('・');
  }

  async fetchProduct(handle) {
    const root = window.Shopify?.routes?.root || '/';
    try {
      const response = await fetch(`${root}products/${encodeURIComponent(handle)}.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      return null;
    }
  }

  setupSteps() {
    const form = this.querySelector('form[data-consult-form]');
    if (!(form instanceof HTMLFormElement)) return;
    const step1 = form.querySelector('[data-consult-step="1"]');
    const step2 = form.querySelector('[data-consult-step="2"]');
    const next = form.querySelector('[data-consult-next]');
    const back = form.querySelector('[data-consult-back]');
    const body = form.querySelector('[data-consult-body]');
    const email = form.querySelector('[data-consult-email]');
    const submit = form.querySelector('[data-consult-submit]');
    const carrier = form.querySelector('[data-consult-carrier]');
    const reply1 = this.querySelector('[data-consult-reply-step="1"]');
    const reply2 = this.querySelector('[data-consult-reply-step="2"]');
    const dmArea = this.querySelector('[data-consult-dm-area]');
    if (!(step1 instanceof HTMLElement) || !(step2 instanceof HTMLElement)) return;
    if (!(next instanceof HTMLButtonElement) || !(body instanceof HTMLTextAreaElement)) return;

    const show = (step) => {
      step1.hidden = step !== 1;
      step2.hidden = step !== 2;
      if (reply1 instanceof HTMLElement) reply1.hidden = step !== 1;
      if (reply2 instanceof HTMLElement) reply2.hidden = step !== 2;
      // 送り先を入れているあいだは、DM の案内をしまう
      if (dmArea instanceof HTMLElement) dmArea.hidden = step !== 1;
    };
    const syncNext = () => {
      next.disabled = body.value.trim() === '';
    };
    const syncEmail = () => {
      const value = email instanceof HTMLInputElement ? email.value.trim() : '';
      if (submit instanceof HTMLButtonElement) submit.disabled = !EMAIL.test(value);
      if (carrier instanceof HTMLElement) carrier.hidden = !CARRIER_DOMAINS.test(value);
    };

    next.hidden = false;
    if (back instanceof HTMLElement) back.hidden = false;
    // 送信でエラーが返ってきたときは、送り先の画面から始める
    show(form.querySelector('[data-consult-errors]') && body.value.trim() !== '' ? 2 : 1);
    syncNext();
    syncEmail();

    let started = false;
    body.addEventListener('input', () => {
      syncNext();
      if (!started && body.value.trim() !== '') {
        started = true;
        sendEvent('consult_start', this.eventParams());
      }
    });
    email?.addEventListener('input', syncEmail);

    next.addEventListener('click', () => {
      if (body.value.trim() === '') return;
      show(2);
      sendEvent('consult_next', this.eventParams());
      const title = form.querySelector('[data-consult-step-title]');
      if (title instanceof HTMLElement) title.focus({ preventScroll: true });
      this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    back?.addEventListener('click', () => {
      show(1);
      body.focus({ preventScroll: true });
    });

    form.addEventListener('submit', () => {
      const value = email instanceof HTMLInputElement ? email.value.trim() : '';
      if (EMAIL.test(value)) identify(value);
      save({ ...this.context, tracked: false });
    });
  }
}

/* ---- 商品ページのブロック ---- */

class ConsultProduct extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section, dialog');
    this.section?.addEventListener('variant:update', this.onVariantUpdate);
  }

  disconnectedCallback() {
    this.section?.removeEventListener('variant:update', this.onVariantUpdate);
  }

  onVariantUpdate = (event) => {
    const data = event.detail?.data;
    if (!data?.html) return;
    if (!data.newProduct && data.productId && this.dataset.productId && data.productId !== this.dataset.productId) {
      return;
    }
    const fresh = data.html.getElementById(this.id);
    if (!(fresh instanceof HTMLElement)) return;
    this.innerHTML = fresh.innerHTML;
    // 在庫切れの表示に変わったら、計測の「mode」も合わせる
    this.dataset.consultMode = fresh.dataset.consultMode || '';
    this.dataset.consultTopic = fresh.dataset.consultTopic || '';
  };
}

/* ---- 送ったあと ---- */

class ConsultThanks extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = 'true';

    const saved = readSaved();
    if (saved) {
      this.showBack(saved);
      if (!saved.tracked) {
        track('Submitted Consultation', {
          topic: saved.topic,
          from: saved.from,
          product: saved.product,
          result: saved.result,
        });
        this.sendLead(saved);
        save({ ...saved, tracked: true });
      }
      return;
    }

    // Shopify Forms から移ってきたとき（テーマのフォームを通らないので、相談ページのアドレスから読む）
    const fromReferrer = this.leadFromReferrer();
    if (fromReferrer) this.sendLead(fromReferrer);
  }

  showBack(saved) {
    const back = this.querySelector('[data-consult-back-to]');
    const target = saved.productUrl || saved.entryUrl;
    if (!(back instanceof HTMLAnchorElement) || !target) return;
    back.href = target;
    back.textContent = saved.productTitle ? `${saved.productTitle}に戻る` : BACK_LABELS[saved.from] || '見ていたページに戻る';
    back.hidden = false;
  }

  sendLead(context) {
    sendEvent('generate_lead', {
      lead_source: 'consult',
      consult_from: context.from || 'none',
      consult_topic: context.topic,
      consult_attached: attachedOf(context),
    });
  }

  leadFromReferrer() {
    try {
      const referrer = new URL(document.referrer);
      if (referrer.origin !== window.location.origin || !/\/pages\/soudan\/?$/.test(referrer.pathname)) return null;
      // 同じ送信でお礼のページを開き直したときは数えない
      const key = `consult:lead:${referrer.href}`;
      if (session()?.getItem(key)) return null;
      session()?.setItem(key, '1');
      const params = referrer.searchParams;
      return {
        from: params.get('from') || '',
        topic: params.get('topic') || '',
        product: params.get('product') || '',
        result: params.get('result') || '',
      };
    } catch (error) {
      return null;
    }
  }
}

observeBlocks();
document.addEventListener('click', onDocumentClick);

if (!customElements.get('consult-compose')) customElements.define('consult-compose', ConsultCompose);
if (!customElements.get('consult-product')) customElements.define('consult-product', ConsultProduct);
if (!customElements.get('consult-thanks')) customElements.define('consult-thanks', ConsultThanks);
